import { Box, SxProps, Theme, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Collapsible from './collapsible';

type Tab = {
  label: React.ReactNode;
  valueLabel?: React.ReactNode;
  value: string;
  content: React.ReactNode;
  icon?: React.ReactNode;
  external?: boolean;
  hide?: boolean;
};

type TabsHeaderProps = {
  tabs: Tab[];
  sx?: SxProps<Theme>;
};

const PREFIX = 'TabsHeader';
const classes = {
  root: `${PREFIX}-root`,
  tabsContainer: `${PREFIX}-tabsContainer`,
  contentContainer: `${PREFIX}-contentContainer`,
  tab: `${PREFIX}-tab`,
  activeTab: `${PREFIX}-activeTab`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
  [`& .${classes.tabsContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
  },
  [`& .${classes.contentContainer}`]: {
    flexGrow: 1,
    padding: theme.spacing(2),
  },
  [`& .${classes.tab}`]: {
    cursor: 'pointer',
    padding: theme.spacing(4),
    marginTop: 8,
    minWidth: 300,
    color: theme.palette.common.white,
    textAlign: 'left',
    '&:hover': {},
  },
  [`& .${classes.activeTab}`]: {
    backgroundColor: '#ffffff16',
    fontWeight: theme.typography.fontWeightMedium,
    borderRadius: 8,
  },
}));

const SubTabs: React.FC<TabsHeaderProps> = ({ tabs, sx }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const initialTab = tabs.find((tab) => location.pathname.endsWith(tab.value))?.value || tabs[0]?.value;
  const [activeTab, setActiveTab] = useState<string>(initialTab);

  return (
    <StyledBox sx={sx}>
      {!isMobile ? (
        <>
          <Box className={classes.tabsContainer}>
            {tabs.map((tab) =>
              tab.hide ? null : (
                <Box
                  key={tab.value}
                  className={`${classes.tab} ${activeTab === tab.value ? classes.activeTab : ''}`}
                  onClick={() => setActiveTab(tab.value)}>
                  {tab.icon} {tab.label}
                  <Box>{activeTab !== tab.value ? tab.valueLabel : ''}</Box>
                </Box>
              )
            )}
          </Box>
          <div className={classes.contentContainer}>{tabs.find((tab) => tab.value === activeTab)?.content}</div>
        </>
      ) : (
        tabs.map((tab) =>
          tab.hide ? null : (
            <Collapsible
              key={tab.value}
              label={typeof tab.label === 'string' ? tab.label : 'Tab'}
              hideBorder
              open={activeTab === tab.value}
              onClick={() => setActiveTab(tab.value)}>
              <Box className={classes.contentContainer}>{tab.content}</Box>
            </Collapsible>
          )
        )
      )}
    </StyledBox>
  );
};

export default React.memo(SubTabs);
